/**
 * OpenAI Realtime API (WebRTC) — experimental voice path for Symptom Checker 3.
 * @see https://platform.openai.com/docs/guides/realtime-webrtc
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { firebaseApp, FUNCTIONS_REGION } from "@healthdesk/shared-services";
import { ensureFirebaseUserForLiveVoice } from "../../services/voice/geminiLiveSession";
import { SUPPORTED_LANGUAGES } from "../../features/patient/components/symptomCheck/voice-conversation";
import { scheduleVerbalEndConversationAck } from "./scheduleVerbalEndConversationAck";

const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";
const OPENING_RESPONSE_DELAY_MS = 400;
const ASSISTANT_TERMINAL_AUDIO_DRAIN_MS = 3200;
const ASSISTANT_AUDIO_IDLE_WINDOW_MS = 1800;
// Keep this long enough to avoid speaker->mic feedback bursts, but shorter
// than the previous 2200ms setting so turn-taking still feels responsive.
const ASSISTANT_POST_DRAIN_GUARD_MS = 1600;
const TERMINAL_DRAIN_RECHECK_MS = 300;
const TERMINAL_DRAIN_MAX_MS = 15_000;
const OPENAI_REALTIME_TRANSCRIPTION_MODEL = "whisper-1";
const OPENAI_REALTIME_SESSION_INSTRUCTIONS = `
You are the Symptom Checker 3 audio renderer for HealthDesk.

Hard rules:
- Only speak when a client response.create instruction is provided.
- Speak only the provided scripted line; do not add greetings, disclaimers, fillers, follow-up questions, or commentary.
- Do not paraphrase unless unavoidable for speech synthesis; preserve wording as closely as possible.
- Never initiate spontaneous clinical guidance.
`.trim();
const OPENAI_REALTIME_TURN_DETECTION = {
  type: "server_vad",
  create_response: false,
  interrupt_response: false,
  // Wait a little longer before ending user speech turns.
  // Helps reduce mid-sentence commits for longer symptom descriptions.
  silence_duration_ms: 1000,
  prefix_padding_ms: 400,
  threshold: 0.5,
};
const RESPONSE_CANCEL_EVENT_TYPE = "response.cancel";
const OUTPUT_AUDIO_BUFFER_CLEAR_EVENT_TYPE = "output_audio_buffer.clear";
const SESSION_UPDATE_EVENT_TYPE = "session.update";
const RESPONSE_CREATE_EVENT_TYPE = "response.create";
const RESPONSE_CREATED_EVENT_TYPE = "response.created";
const RESPONSE_AUDIO_DELTA_EVENT_TYPES = new Set(["response.audio.delta", "response.output_audio.delta"]);
const RESPONSE_OUTPUT_AUDIO_TRANSCRIPT_DELTA_EVENT_TYPE = "response.output_audio_transcript.delta";
const RESPONSE_OUTPUT_AUDIO_TRANSCRIPT_DONE_EVENT_TYPE = "response.output_audio_transcript.done";
const RESPONSE_METADATA_SOURCE_KEY = "hd_source";
const RESPONSE_METADATA_REQUEST_ID_KEY = "hd_request_id";
const RESPONSE_METADATA_COMPLETE_ON_TERMINAL_KEY = "hd_complete_on_terminal";
const SCRIPTED_RESPONSE_SOURCE_VALUE = "scripted_queue";
const OPENAI_REALTIME_CONTINUE_CONVERSATION_COPY =
  "You can continue this conversation if you want, and I can keep helping while you decide next steps.";
const OPENAI_REALTIME_REPEAT_EMERGENCY_FALLBACK =
  "I hear you. We can continue this conversation if you want. Tell me more about what you're experiencing.";
const OPENAI_REALTIME_EMERGENCY_PATTERN =
  /\b(emergency|urgent|immediate(?:ly)?|seek(?:ing)?\s+medical\s+attention|seek(?:ing)?\s+care|go\s+to\s+the\s+er|go\s+to\s+the\s+emergency\s+department|call\s+911)\b/i;
const OFFSCRIPT_VIOLATION_LIMIT = 3;

/** Server allows only one in-flight Realtime response per conversation; queue the rest. */
const RESPONSE_TERMINAL_TYPES = new Set([
  "response.done",
  "response.completed",
  "response.failed",
  "response.cancelled",
]);

function extractResponseId(msg) {
  return (
    msg?.response?.id ??
    msg?.response_id ??
    (typeof msg?.response === "string" ? msg.response : null) ??
    null
  );
}

function extractResponseMetadata(msg) {
  const m = msg?.response?.metadata;
  if (!m || typeof m !== "object") {
    return null;
  }
  return m;
}

function normalizeWhitespaces(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

function isEmergencyAdvisoryLine(text) {
  return OPENAI_REALTIME_EMERGENCY_PATTERN.test(String(text || ""));
}

function includesContinueConversationCopy(text) {
  return /\bcontinue\s+this\s+conversation\b/i.test(String(text || ""));
}

function extractLastQuestionSentence(text) {
  const src = String(text || "");
  const questionSentences = src.match(/[^?]*\?/g);
  if (!questionSentences || questionSentences.length === 0) return "";
  return normalizeWhitespaces(questionSentences[questionSentences.length - 1]);
}

function ensureSingleEmergencyAdvisoryGuidance(text, emergencyAlreadyAdvisedRef) {
  const normalized = normalizeWhitespaces(text);
  if (!normalized) {
    return normalized;
  }

  if (!isEmergencyAdvisoryLine(normalized)) {
    return normalized;
  }

  if (!emergencyAlreadyAdvisedRef.current) {
    emergencyAlreadyAdvisedRef.current = true;
    if (includesContinueConversationCopy(normalized)) {
      return normalized;
    }
    return `${normalized} ${OPENAI_REALTIME_CONTINUE_CONVERSATION_COPY}`;
  }

  const followUpQuestion = extractLastQuestionSentence(normalized);
  if (followUpQuestion) {
    return `I hear you. We can continue this conversation if you want. ${followUpQuestion}`;
  }
  return OPENAI_REALTIME_REPEAT_EMERGENCY_FALLBACK;
}

function isLikelyClosingLine(text) {
  const normalized = normalizeWhitespaces(text).toLowerCase();
  if (!normalized) return false;
  if (normalized.includes("?")) return false;
  return (
    normalized.includes("thank you") ||
    normalized.includes("analyzing your symptoms") ||
    normalized.includes("analyze your symptoms") ||
    normalized.includes("good understanding of your symptoms") ||
    normalized.includes("review the analysis") ||
    normalized.includes("review the computational analysis") ||
    normalized.includes("designed for adults 18 and older")
  );
}

function isLikelyTranscriptArtifact(text) {
  const normalized = normalizeWhitespaces(text).toLowerCase();
  if (!normalized) return false;
  const hasTranscribedBy = normalized.includes("transcribed by");
  const hasUrlLikeToken =
    /https?:\/\/\S+/i.test(normalized) || /\bwww\.[a-z0-9.-]+\.[a-z]{2,}\b/i.test(normalized);
  // Typical watermark artifacts are short "Transcribed by <url>" lines.
  return hasTranscribedBy && (hasUrlLikeToken || normalized.length <= 96);
}

function useOpenAIRealtimeVoiceConversationEngine({
  onPauseDetected,
  openingMessage = "Hello! How can I help you today?",
  language = "en-US",
  context,
  onComplete,
  onUserSpeech,
  onLogEntry,
  onLiveTranscript,
  askFirstQuestionAfterOpening = false,
  askDisclaimerAfterOpening = true,
  controlledConversationLog,
  onConversationLogChange,
  controlledQuestionsAsked,
  onQuestionsAskedChange,
} = {}) {
  const [isActive, setIsActive] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const [audioConfirmed, setAudioConfirmed] = useState(false);
  const [endConversationAcknowledged, setEndConversationAcknowledged] = useState(false);

  const [questionsAskedInternal, setQuestionsAskedInternal] = useState([]);
  const [conversationLogInternal, setConversationLogInternal] = useState([]);

  const questionsAsked =
    controlledQuestionsAsked !== undefined ? controlledQuestionsAsked : questionsAskedInternal;
  const conversationLog =
    controlledConversationLog !== undefined ? controlledConversationLog : conversationLogInternal;

  const setQuestionsAsked = useCallback(
    (valueOrUpdater) => {
      if (onQuestionsAskedChange) {
        if (typeof valueOrUpdater === "function") {
          onQuestionsAskedChange((prev) => valueOrUpdater(prev));
        } else {
          onQuestionsAskedChange(valueOrUpdater);
        }
      } else {
        setQuestionsAskedInternal(valueOrUpdater);
      }
    },
    [onQuestionsAskedChange]
  );

  const setConversationLog = useCallback(
    (valueOrUpdater) => {
      if (onConversationLogChange) {
        if (typeof valueOrUpdater === "function") {
          onConversationLogChange((prev) => valueOrUpdater(prev));
        } else {
          onConversationLogChange(valueOrUpdater);
        }
      } else {
        setConversationLogInternal(valueOrUpdater);
      }
    },
    [onConversationLogChange]
  );

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const remoteAudioElementsRef = useRef([]);
  const sessionTokenRef = useRef(0);
  const startInFlightRef = useRef(false);
  const isProcessingUserTurnRef = useRef(false);
  /** Pending `response.create` instructions (FIFO). */
  const pendingResponseInstructionsRef = useRef([]);
  /** True while the server has an active response (ours or server/VAD). */
  const responseInFlightRef = useRef(false);
  /** Count of client-issued response.create events waiting for response.created pairing. */
  const pendingClientResponseCreateCountRef = useRef(0);
  const scriptedRequestCounterRef = useRef(0);
  const pendingScriptedRequestIdsRef = useRef(new Set());
  const pendingScriptedRequestTextByIdRef = useRef(new Map());
  const scriptedExpectedTextByResponseIdRef = useRef(new Map());
  const scriptedResponseIdsRef = useRef(new Set());
  const ignoredResponseIdsRef = useRef(new Set());
  const activeAllowedResponseIdRef = useRef(null);
  const offscriptViolationCountRef = useRef(0);
  const activeResponseIdRef = useRef(null);
  const assistantTranscriptByResponseIdRef = useRef(new Map());
  const fallbackAssistantTranscriptRef = useRef("");
  const lastLoggedAssistantTranscriptResponseIdRef = useRef(null);
  /** Request IDs marked for completion when their response reaches terminal. */
  const completionRequestIdsRef = useRef(new Set());
  /** Response IDs marked to complete conversation on terminal. */
  const completionResponseIdsRef = useRef(new Set());
  /** Monotonic user-turn sequence; used to drop stale unsent scripted questions. */
  const latestUserTurnSeqRef = useRef(0);
  /** Avoid double-handling when both `response.done` and `response.completed` refer to the same response. */
  const lastHandledTerminalResponseIdRef = useRef(null);
  /** After a Realtime `error` event, stop sending `response.create` and ignore transcripts until restart. */
  const sessionHaltedRef = useRef(false);
  /** Allow emergency-care advisories once per conversation (then continue without repeating the advisory paragraph). */
  const emergencyAdvisoryGivenRef = useRef(false);
  const postOpeningDisclaimerPendingRef = useRef(false);
  const askDisclaimerAfterOpeningRef = useRef(askDisclaimerAfterOpening);
  const askFirstQuestionAfterOpeningRef = useRef(askFirstQuestionAfterOpening);
  const terminalDrainTimerRef = useRef(null);
  const terminalDrainStartedAtRef = useRef(0);
  const lastAssistantAudioDeltaAtRef = useRef(0);
  const postDrainOpenTimerRef = useRef(null);
  const assistantPostDrainGuardUntilRef = useRef(0);

  const onPauseDetectedRef = useRef(onPauseDetected);
  const contextRef = useRef(context);
  const onUserSpeechRef = useRef(onUserSpeech);
  const onLogEntryRef = useRef(onLogEntry);
  const onLiveTranscriptRef = useRef(onLiveTranscript);
  const onCompleteRef = useRef(onComplete);
  const conversationLogRef = useRef(conversationLog);
  const questionsAskedRef = useRef(questionsAsked);
  const isSpeakingRef = useRef(isSpeaking);
  const stopConversationRef = useRef(null);

  useEffect(() => {
    onPauseDetectedRef.current = onPauseDetected;
    contextRef.current = context;
    onUserSpeechRef.current = onUserSpeech;
    onLogEntryRef.current = onLogEntry;
    onLiveTranscriptRef.current = onLiveTranscript;
    onCompleteRef.current = onComplete;
  }, [onPauseDetected, context, onUserSpeech, onLogEntry, onLiveTranscript, onComplete]);

  useEffect(() => {
    conversationLogRef.current = conversationLog;
  }, [conversationLog]);

  useEffect(() => {
    questionsAskedRef.current = questionsAsked;
  }, [questionsAsked]);

  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);

  useEffect(() => {
    askDisclaimerAfterOpeningRef.current = askDisclaimerAfterOpening;
  }, [askDisclaimerAfterOpening]);

  useEffect(() => {
    askFirstQuestionAfterOpeningRef.current = askFirstQuestionAfterOpening;
  }, [askFirstQuestionAfterOpening]);

  const appendLog = useCallback(
    (entry) => {
      setConversationLog((prev) => {
        const next = [...prev, { ...entry, timestamp: Date.now() }];
        conversationLogRef.current = next;
        onLogEntryRef.current?.(entry, next);
        return next;
      });
    },
    [setConversationLog]
  );

  const sendDataChannelEvent = useCallback((event, logLabel) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== "open") {
      return false;
    }
    try {
      dc.send(JSON.stringify(event));
      return true;
    } catch (err) {
      console.warn(`[OpenAI Realtime] ${logLabel} send failed`, err);
      return false;
    }
  }, []);

  const sendSessionUpdate = useCallback(() => {
    return sendDataChannelEvent(
      {
        type: SESSION_UPDATE_EVENT_TYPE,
        session: {
          type: "realtime",
          instructions: OPENAI_REALTIME_SESSION_INSTRUCTIONS,
          audio: {
            input: {
              transcription: { model: OPENAI_REALTIME_TRANSCRIPTION_MODEL },
              turn_detection: OPENAI_REALTIME_TURN_DETECTION,
            },
          },
        },
      },
      SESSION_UPDATE_EVENT_TYPE
    );
  }, [sendDataChannelEvent]);

  const sendResponseCancel = useCallback(
    (responseId) => {
      const payload =
        responseId && typeof responseId === "string"
          ? { type: RESPONSE_CANCEL_EVENT_TYPE, response_id: responseId }
          : { type: RESPONSE_CANCEL_EVENT_TYPE };
      return sendDataChannelEvent(payload, RESPONSE_CANCEL_EVENT_TYPE);
    },
    [sendDataChannelEvent]
  );

  const sendOutputAudioBufferClear = useCallback(() => {
    return sendDataChannelEvent(
      { type: OUTPUT_AUDIO_BUFFER_CLEAR_EVENT_TYPE },
      OUTPUT_AUDIO_BUFFER_CLEAR_EVENT_TYPE
    );
  }, [sendDataChannelEvent]);

  const completeConversationNow = useCallback(() => {
    completionRequestIdsRef.current = new Set();
    completionResponseIdsRef.current = new Set();
    onCompleteRef.current?.(conversationLogRef.current);
    stopConversationRef.current?.();
  }, []);

  const stopAllRemoteAudio = useCallback(() => {
    const els = remoteAudioElementsRef.current || [];
    for (const el of els) {
      try {
        el.pause();
      } catch (_) {
        /* ignore */
      }
      try {
        el.srcObject = null;
      } catch (_) {
        /* ignore */
      }
      try {
        el.remove();
      } catch (_) {
        /* ignore */
      }
    }
    remoteAudioElementsRef.current = [];
  }, []);

  const setAudioGateOpen = useCallback((responseId) => {
    const els = remoteAudioElementsRef.current || [];
    for (const el of els) {
      try {
        el.volume = 1;
        el.muted = false;
      } catch (_) {
        /* ignore */
      }
    }
    console.warn("[OpenAI Realtime] audio_gate_opened_for_scripted_response", {
      responseId: responseId || null,
    });
  }, []);

  const setAudioGateClosed = useCallback((reason = "unknown", responseId = null) => {
    const els = remoteAudioElementsRef.current || [];
    for (const el of els) {
      try {
        el.volume = 0;
        el.muted = true;
      } catch (_) {
        /* ignore */
      }
    }
    console.warn("[OpenAI Realtime] audio_gate_closed", {
      reason,
      responseId,
    });
  }, []);

  const setMicrophoneCaptureEnabled = useCallback((enabled, reason = "unknown") => {
    const stream = mediaStreamRef.current;
    if (!stream) return;
    for (const track of stream.getAudioTracks()) {
      try {
        track.enabled = Boolean(enabled);
      } catch (_) {
        /* ignore */
      }
    }
    console.warn("[OpenAI Realtime] mic_capture_toggled", {
      enabled: Boolean(enabled),
      reason,
    });
  }, []);

  const clearTerminalDrainTimer = useCallback(() => {
    if (terminalDrainTimerRef.current) {
      window.clearTimeout(terminalDrainTimerRef.current);
      terminalDrainTimerRef.current = null;
    }
  }, []);

  const clearPostDrainOpenTimer = useCallback(() => {
    if (postDrainOpenTimerRef.current) {
      window.clearTimeout(postDrainOpenTimerRef.current);
      postDrainOpenTimerRef.current = null;
    }
  }, []);

  const flushPendingResponseCreates = useCallback(() => {
    if (sessionHaltedRef.current) {
      return;
    }
    const dc = dcRef.current;
    if (!dc || dc.readyState !== "open") {
      return;
    }
    if (responseInFlightRef.current) {
      return;
    }
    const queue = pendingResponseInstructionsRef.current;
    if (queue.length === 0) {
      return;
    }
    let queueEntry = null;
    while (queue.length > 0) {
      const nextEntry = queue.shift();
      const normalizedEntry =
        nextEntry && typeof nextEntry === "object"
          ? nextEntry
          : {
              instructionText: String(nextEntry || ""),
              requestCompletionOnTerminal: false,
              turnSeq: 0,
            };
      const entryTurnSeq = Number(normalizedEntry.turnSeq || 0);
      if (entryTurnSeq < latestUserTurnSeqRef.current) {
        console.warn("[OpenAI Realtime] dropped stale queued question", {
          entryTurnSeq,
          latestTurnSeq: latestUserTurnSeqRef.current,
        });
        continue;
      }
      queueEntry = normalizedEntry;
      break;
    }
    if (!queueEntry) {
      return;
    }
    const instructionText = ensureSingleEmergencyAdvisoryGuidance(
      queueEntry.instructionText,
      emergencyAdvisoryGivenRef
    );
    const safe = String(instructionText || "").replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const requestId = `sc3_scripted_${Date.now()}_${++scriptedRequestCounterRef.current}`;
    const requestCompletionOnTerminal = Boolean(queueEntry.requestCompletionOnTerminal);
    // GA Realtime: per-response field is `output_modalities` (not beta `modalities`).
    // Session is already minted with output_modalities: ["audio"]; omit here to inherit.
    responseInFlightRef.current = true;
    const sent = sendDataChannelEvent(
      {
        type: RESPONSE_CREATE_EVENT_TYPE,
        response: {
          instructions: `You are a speech renderer. Output exactly this text, verbatim, preserving wording and punctuation. Do not paraphrase, summarize, soften, or add empathy/disclaimers. Do not prepend or append anything. Exact text: "${safe}"`,
          metadata: {
            [RESPONSE_METADATA_SOURCE_KEY]: SCRIPTED_RESPONSE_SOURCE_VALUE,
            [RESPONSE_METADATA_REQUEST_ID_KEY]: requestId,
            [RESPONSE_METADATA_COMPLETE_ON_TERMINAL_KEY]: requestCompletionOnTerminal ? "1" : "0",
          },
        },
      },
      RESPONSE_CREATE_EVENT_TYPE
    );
    if (sent) {
      pendingClientResponseCreateCountRef.current += 1;
      pendingScriptedRequestIdsRef.current.add(requestId);
      pendingScriptedRequestTextByIdRef.current.set(requestId, String(instructionText || ""));
      if (requestCompletionOnTerminal) {
        completionRequestIdsRef.current.add(requestId);
      }
    } else {
      responseInFlightRef.current = false;
    }
  }, [sendDataChannelEvent]);

  const sendModelResponse = useCallback(
    (instructionText, options = {}) => {
      if (sessionHaltedRef.current) {
        return;
      }
      const dc = dcRef.current;
      if (!dc || dc.readyState !== "open") {
        console.warn("[OpenAI Realtime] data channel not open; cannot queue response.create");
        return;
      }
      pendingResponseInstructionsRef.current.push({
        instructionText: String(instructionText ?? ""),
        requestCompletionOnTerminal: Boolean(options.requestCompletionOnTerminal),
        turnSeq: Number(options.turnSeq || latestUserTurnSeqRef.current || 0),
      });
      flushPendingResponseCreates();
    },
    [flushPendingResponseCreates]
  );

  const runUserTurn = useCallback(
    async (userText, triggerSource = "unknown") => {
      if (sessionHaltedRef.current) return;
      if (isProcessingUserTurnRef.current) return;
      const trimmed = String(userText || "").trim();
      if (trimmed && isLikelyTranscriptArtifact(trimmed)) {
        console.warn("[OpenAI Realtime] dropped transcript artifact", {
          transcript: trimmed,
        });
        return;
      }
      const turnSeq = ++latestUserTurnSeqRef.current;
      let queuedAssistantQuestion = false;
      isProcessingUserTurnRef.current = true;
      setIsSpeaking(false);
      setIsListening(false);
      setMicrophoneCaptureEnabled(false, "user_turn_processing");
      // Preserve only the latest-turn questioning intent; older unsent items are stale.
      pendingResponseInstructionsRef.current = [];
      try {
        if (trimmed) {
          appendLog({ speaker: "user", text: trimmed });
          onUserSpeechRef.current?.(trimmed);
        }
        onLiveTranscriptRef.current?.("");

        // App-owned conversation brain: OpenAI Realtime only renders scripted output.
        const handler = onPauseDetectedRef.current;
        if (!handler) return;

        const result = await handler({
          userTranscript: trimmed,
          conversationLog: conversationLogRef.current,
          questionsAsked: questionsAskedRef.current,
          context: contextRef.current,
        });

        if (!result) return;

        let requestCompletionOnTerminal = false;
        const stopImmediately = !!result.stopImmediately;
        if (result.question) {
          queuedAssistantQuestion = true;
          const q = String(result.question).trim();
          // Keep orchestration keyed on scripted question text.
          // Visible assistant transcript is appended from response.output_audio_transcript.done.
          setQuestionsAsked((prev) => (prev.includes(q) ? prev : [...prev, q]));
          setIsSpeaking(true);
          setMicrophoneCaptureEnabled(false, "assistant_turn_started");
          if (result.isComplete && !stopImmediately) {
            const completionLine = String(result.question || "").trim();
            if (isLikelyClosingLine(completionLine)) {
              requestCompletionOnTerminal = true;
            } else {
              // Defensive guard: keep session alive when upstream accidentally marks
              // a follow-up question as complete.
              console.warn("[OpenAI Realtime] ignored non-closing completion line", {
                completionLine,
              });
            }
          }
          sendModelResponse(q, { requestCompletionOnTerminal, turnSeq });
        }

        if (result.isComplete) {
          if (stopImmediately) {
            scheduleVerbalEndConversationAck(setEndConversationAcknowledged, completeConversationNow);
          } else if (!result.question) {
            completeConversationNow();
          }
        }
      } catch (err) {
        console.error("[OpenAI Realtime] runUserTurn error", err);
        setError(err?.message || "Voice turn failed");
      } finally {
        isProcessingUserTurnRef.current = false;
        if (
          !queuedAssistantQuestion &&
          !responseInFlightRef.current &&
          !activeAllowedResponseIdRef.current
        ) {
          setIsListening(true);
          setMicrophoneCaptureEnabled(true, "awaiting_user_after_no_output");
        }
      }
    },
    [appendLog, completeConversationNow, sendModelResponse, setMicrophoneCaptureEnabled, setQuestionsAsked]
  );

  const handleDataMessage = useCallback(
    (raw) => {
      let msg;
      try {
        msg = JSON.parse(raw);
      } catch (e) {
        console.warn("[OpenAI Realtime] non-JSON data channel message", e);
        return;
      }
      const type = msg?.type || "";
      const responseId = extractResponseId(msg);
      const responseMetadata = extractResponseMetadata(msg);

      if (type === RESPONSE_CREATED_EVENT_TYPE) {
        responseInFlightRef.current = true;
        activeResponseIdRef.current = responseId;
        const metadataSource = responseMetadata?.[RESPONSE_METADATA_SOURCE_KEY];
        const metadataRequestId = responseMetadata?.[RESPONSE_METADATA_REQUEST_ID_KEY];
        const metadataSaysScripted = metadataSource === SCRIPTED_RESPONSE_SOURCE_VALUE;
        const hasKnownRequestId =
          typeof metadataRequestId === "string" &&
          pendingScriptedRequestIdsRef.current.has(metadataRequestId);
        const isClientInitiated = metadataSaysScripted && hasKnownRequestId;
        if (isClientInitiated) {
          if (pendingClientResponseCreateCountRef.current > 0) {
            pendingClientResponseCreateCountRef.current -= 1;
          }
          pendingScriptedRequestIdsRef.current.delete(metadataRequestId);
          if (responseId) {
            const expectedTextForRequest = pendingScriptedRequestTextByIdRef.current.get(metadataRequestId) || "";
            pendingScriptedRequestTextByIdRef.current.delete(metadataRequestId);
            scriptedExpectedTextByResponseIdRef.current.set(responseId, expectedTextForRequest);
            scriptedResponseIdsRef.current.add(responseId);
            activeAllowedResponseIdRef.current = responseId;
            offscriptViolationCountRef.current = 0;
            lastAssistantAudioDeltaAtRef.current = Date.now();
            setMicrophoneCaptureEnabled(false, "assistant_response_created");
            setAudioGateOpen(responseId);
            const requestId = metadataRequestId;
            if (requestId && completionRequestIdsRef.current.has(requestId)) {
              completionRequestIdsRef.current.delete(requestId);
              completionResponseIdsRef.current.add(responseId);
            }
          }
        } else {
          offscriptViolationCountRef.current += 1;
          console.warn("[OpenAI Realtime] offscript_response_created_blocked", {
            responseId,
            event_id: msg?.event_id,
            metadataSource: metadataSource || null,
            metadataRequestId:
              typeof metadataRequestId === "string" ? metadataRequestId : null,
            knownPendingScriptedRequestIds: pendingScriptedRequestIdsRef.current.size,
            violationCount: offscriptViolationCountRef.current,
          });
          if (responseId) {
            ignoredResponseIdsRef.current.add(responseId);
          }
          const cancelSent = sendResponseCancel(responseId);
          if (cancelSent) {
            sendOutputAudioBufferClear();
            setAudioGateClosed("offscript_response_created", responseId || null);
          }
          if (offscriptViolationCountRef.current >= OFFSCRIPT_VIOLATION_LIMIT) {
            console.warn("[OpenAI Realtime] offscript_violation_limit_reached", {
              violationCount: offscriptViolationCountRef.current,
            });
            sessionHaltedRef.current = true;
            pendingResponseInstructionsRef.current = [];
            stopConversationRef.current?.();
          }
          return;
        }
      }
      if (RESPONSE_AUDIO_DELTA_EVENT_TYPES.has(type)) {
        if (responseId && ignoredResponseIdsRef.current.has(responseId)) {
          return;
        }
        if (
          responseId &&
          responseId !== activeAllowedResponseIdRef.current
        ) {
          // Hard guardrail: block any non-scripted assistant audio so only app-authored
          // SC3 questions are spoken.
          offscriptViolationCountRef.current += 1;
          console.warn("[OpenAI Realtime] offscript_audio_delta_blocked", {
            responseId,
            event_id: msg?.event_id,
            activeAllowedResponseId: activeAllowedResponseIdRef.current,
            violationCount: offscriptViolationCountRef.current,
          });
          ignoredResponseIdsRef.current.add(responseId);
          const cancelSent = sendResponseCancel(responseId);
          if (cancelSent) {
            sendOutputAudioBufferClear();
            setAudioGateClosed("offscript_audio_delta", responseId);
          }
          if (offscriptViolationCountRef.current >= OFFSCRIPT_VIOLATION_LIMIT) {
            console.warn("[OpenAI Realtime] offscript_violation_limit_reached", {
              violationCount: offscriptViolationCountRef.current,
            });
            sessionHaltedRef.current = true;
            pendingResponseInstructionsRef.current = [];
            stopConversationRef.current?.();
          }
          return;
        }
        lastAssistantAudioDeltaAtRef.current = Date.now();
        setIsSpeaking(true);
      }
      if (type === RESPONSE_OUTPUT_AUDIO_TRANSCRIPT_DELTA_EVENT_TYPE) {
        if (responseId && ignoredResponseIdsRef.current.has(responseId)) {
          return;
        }
        const delta = typeof msg?.delta === "string" ? msg.delta : "";
        if (delta) {
          if (responseId) {
            const prior = assistantTranscriptByResponseIdRef.current.get(responseId) || "";
            assistantTranscriptByResponseIdRef.current.set(responseId, prior + delta);
          } else {
            fallbackAssistantTranscriptRef.current += delta;
          }
        }
      }
      if (type === RESPONSE_OUTPUT_AUDIO_TRANSCRIPT_DONE_EVENT_TYPE) {
        if (responseId && ignoredResponseIdsRef.current.has(responseId)) {
          return;
        }
        if (responseId && responseId !== activeAllowedResponseIdRef.current) {
          console.warn("[OpenAI Realtime] offscript_transcript_done_blocked", {
            responseId,
            event_id: msg?.event_id,
            activeAllowedResponseId: activeAllowedResponseIdRef.current,
          });
          return;
        }
        const transcriptFromEvent = typeof msg?.transcript === "string" ? msg.transcript.trim() : "";
        const transcriptFromDelta = responseId
          ? assistantTranscriptByResponseIdRef.current.get(responseId) || ""
          : fallbackAssistantTranscriptRef.current;
        const vocalized = (transcriptFromEvent || transcriptFromDelta || "").trim();
        if (responseId) {
          assistantTranscriptByResponseIdRef.current.delete(responseId);
        }
        fallbackAssistantTranscriptRef.current = "";
        if (!vocalized) {
          return;
        }
        if (
          responseId &&
          lastLoggedAssistantTranscriptResponseIdRef.current &&
          lastLoggedAssistantTranscriptResponseIdRef.current === responseId
        ) {
          return;
        }
        if (responseId) {
          lastLoggedAssistantTranscriptResponseIdRef.current = responseId;
        }
        const expectedText = responseId
          ? scriptedExpectedTextByResponseIdRef.current.get(responseId) || ""
          : "";
        const normalizeForCompare = (value) =>
          String(value || "")
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, " ")
            .replace(/\s+/g, " ")
            .trim();
        const expectedNorm = normalizeForCompare(expectedText);
        const spokenNorm = normalizeForCompare(vocalized);
        const scriptedMismatch =
          Boolean(expectedNorm) &&
          Boolean(spokenNorm) &&
          expectedNorm !== spokenNorm &&
          !spokenNorm.includes(expectedNorm) &&
          !expectedNorm.includes(spokenNorm);
        const textForConversationLog = scriptedMismatch && expectedText ? expectedText : vocalized;
        appendLog({ speaker: "assistant", text: textForConversationLog });
      }
      if (RESPONSE_TERMINAL_TYPES.has(type)) {
        const duplicateTerminal = responseId && lastHandledTerminalResponseIdRef.current === responseId;
        if (!duplicateTerminal) {
          const completeOnTerminal = Boolean(
            responseId && completionResponseIdsRef.current.has(responseId)
          );
          if (responseId) {
            lastHandledTerminalResponseIdRef.current = responseId;
            assistantTranscriptByResponseIdRef.current.delete(responseId);
            scriptedResponseIdsRef.current.delete(responseId);
            scriptedExpectedTextByResponseIdRef.current.delete(responseId);
            ignoredResponseIdsRef.current.delete(responseId);
            completionResponseIdsRef.current.delete(responseId);
            if (activeAllowedResponseIdRef.current === responseId) {
              activeAllowedResponseIdRef.current = null;
            }
            if (activeResponseIdRef.current === responseId) {
              activeResponseIdRef.current = null;
            }
          }
          responseInFlightRef.current = false;
          // Do not mute immediately on terminal; final audio packets can still be
          // rendering. Premature gate close truncates spoken output.
          clearTerminalDrainTimer();
          terminalDrainStartedAtRef.current = Date.now();
          terminalDrainTimerRef.current = window.setTimeout(() => {
            const finalizeDrainIfReady = () => {
              terminalDrainTimerRef.current = null;
              if (
                sessionHaltedRef.current ||
                responseInFlightRef.current ||
                Boolean(activeAllowedResponseIdRef.current)
              ) {
                return;
              }
              const now = Date.now();
              const elapsedSinceStart = now - terminalDrainStartedAtRef.current;
              const elapsedSinceLastDelta = now - (lastAssistantAudioDeltaAtRef.current || 0);
              const deltaQuiet =
                lastAssistantAudioDeltaAtRef.current === 0 ||
                elapsedSinceLastDelta >= ASSISTANT_AUDIO_IDLE_WINDOW_MS;
              if (!deltaQuiet && elapsedSinceStart < TERMINAL_DRAIN_MAX_MS) {
                terminalDrainTimerRef.current = window.setTimeout(
                  finalizeDrainIfReady,
                  TERMINAL_DRAIN_RECHECK_MS
                );
                return;
              }
              setIsSpeaking(false);
              setIsListening(false);
              setMicrophoneCaptureEnabled(false, "assistant_post_drain_guard");
              assistantPostDrainGuardUntilRef.current = Date.now() + ASSISTANT_POST_DRAIN_GUARD_MS;
              clearPostDrainOpenTimer();
              postDrainOpenTimerRef.current = window.setTimeout(() => {
                postDrainOpenTimerRef.current = null;
                if (
                  sessionHaltedRef.current ||
                  responseInFlightRef.current ||
                  Boolean(activeAllowedResponseIdRef.current)
                ) {
                  return;
                }
                if (postOpeningDisclaimerPendingRef.current && askDisclaimerAfterOpeningRef.current) {
                  postOpeningDisclaimerPendingRef.current = false;
                  void runUserTurn("", "disclaimer_after_opening");
                  return;
                }
                setIsListening(true);
                setMicrophoneCaptureEnabled(true, "assistant_response_terminal_drain");
                flushPendingResponseCreates();
                if (
                  completeOnTerminal &&
                  pendingResponseInstructionsRef.current.length === 0 &&
                  !responseInFlightRef.current
                ) {
                  completeConversationNow();
                }
              }, ASSISTANT_POST_DRAIN_GUARD_MS);
            };
            finalizeDrainIfReady();
          }, ASSISTANT_TERMINAL_AUDIO_DRAIN_MS);
        }
      }
      if (type === "error") {
        responseInFlightRef.current = false;
        sessionHaltedRef.current = true;
        pendingResponseInstructionsRef.current = [];
        activeAllowedResponseIdRef.current = null;
        setMicrophoneCaptureEnabled(false, "server_error");
        setAudioGateClosed("server_error", responseId || null);
        const err = msg?.error && typeof msg.error === "object" ? msg.error : {};
        const m = err?.message || msg?.message || "Realtime error";
        console.warn("[OpenAI Realtime] server error", {
          code: err?.code,
          type: err?.type,
          message: m,
          param: err?.param,
          event_id: msg?.event_id,
        });
        setError(m);
        // Do not flush here: immediate retry often re-triggers the same error and floods the console.
        // `response.done` / `response.failed` will clear in-flight and flush if the session recovers.
      }

      if (
        type === "conversation.item.input_audio_transcription.completed" &&
        typeof msg.transcript === "string" &&
        msg.transcript.trim()
      ) {
        if (!sessionHaltedRef.current) {
          const transcript = String(msg.transcript || "").trim();
          if (isLikelyTranscriptArtifact(transcript)) {
            console.warn("[OpenAI Realtime] ignored transcription artifact event", {
              transcript,
            });
            return;
          }
          const assistantOutputActive =
            responseInFlightRef.current ||
            Boolean(activeAllowedResponseIdRef.current) ||
            isSpeakingRef.current ||
            Date.now() < assistantPostDrainGuardUntilRef.current;
          if (assistantOutputActive) {
            // Ignore mic transcripts captured while assistant audio is still rendering.
            // Without this guard, tiny echoed fragments can be treated as a new user turn
            // and prematurely mute/cancel the current spoken question.
            return;
          }
          // User continued speaking; cancel pending completion markers.
          completionRequestIdsRef.current = new Set();
          completionResponseIdsRef.current = new Set();
          activeAllowedResponseIdRef.current = null;
          setAudioGateClosed("user_turn_started", responseId || null);
          setMicrophoneCaptureEnabled(false, "user_turn_started");
          void runUserTurn(transcript, "input_audio_transcription.completed");
        }
      }
    },
    [
      appendLog,
      clearTerminalDrainTimer,
      completeConversationNow,
      flushPendingResponseCreates,
      runUserTurn,
      setMicrophoneCaptureEnabled,
      sendOutputAudioBufferClear,
      sendResponseCancel,
    ]
  );

  const stopConversation = useCallback(() => {
    // Invalidate handlers from any older/parallel session immediately.
    sessionTokenRef.current += 1;
    startInFlightRef.current = false;
    isProcessingUserTurnRef.current = false;
    latestUserTurnSeqRef.current = 0;
    clearTerminalDrainTimer();
    clearPostDrainOpenTimer();
    assistantPostDrainGuardUntilRef.current = 0;
    setMicrophoneCaptureEnabled(false, "stop_conversation");
    try {
      if (activeResponseIdRef.current) {
        sendResponseCancel(activeResponseIdRef.current);
      } else {
        sendResponseCancel();
      }
      sendOutputAudioBufferClear();
      setAudioGateClosed("stop_conversation", activeResponseIdRef.current || null);
    } catch (_) {
      /* ignore */
    }
    try {
      dcRef.current?.close();
    } catch (_) {
      /* ignore */
    }
    dcRef.current = null;
    try {
      pcRef.current?.getSenders().forEach((s) => s.track?.stop());
      pcRef.current?.close();
    } catch (_) {
      /* ignore */
    }
    pcRef.current = null;
    try {
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    } catch (_) {
      /* ignore */
    }
    mediaStreamRef.current = null;
    stopAllRemoteAudio();
    pendingResponseInstructionsRef.current = [];
    responseInFlightRef.current = false;
    pendingClientResponseCreateCountRef.current = 0;
    pendingScriptedRequestIdsRef.current = new Set();
    pendingScriptedRequestTextByIdRef.current = new Map();
    scriptedExpectedTextByResponseIdRef.current = new Map();
    scriptedRequestCounterRef.current = 0;
    activeAllowedResponseIdRef.current = null;
    offscriptViolationCountRef.current = 0;
    activeResponseIdRef.current = null;
    scriptedResponseIdsRef.current = new Set();
    ignoredResponseIdsRef.current = new Set();
    assistantTranscriptByResponseIdRef.current = new Map();
    fallbackAssistantTranscriptRef.current = "";
    lastLoggedAssistantTranscriptResponseIdRef.current = null;
    terminalDrainStartedAtRef.current = 0;
    lastAssistantAudioDeltaAtRef.current = 0;
    completionRequestIdsRef.current = new Set();
    completionResponseIdsRef.current = new Set();
    lastHandledTerminalResponseIdRef.current = null;
    sessionHaltedRef.current = false;
    emergencyAdvisoryGivenRef.current = false;
    postOpeningDisclaimerPendingRef.current = false;
    const teardownClean =
      !dcRef.current &&
      !pcRef.current &&
      !mediaStreamRef.current &&
      pendingResponseInstructionsRef.current.length === 0 &&
      pendingScriptedRequestIdsRef.current.size === 0 &&
      pendingScriptedRequestTextByIdRef.current.size === 0 &&
      scriptedExpectedTextByResponseIdRef.current.size === 0 &&
      activeAllowedResponseIdRef.current === null &&
      remoteAudioElementsRef.current.length === 0;
    if (!teardownClean) {
      console.warn("[OpenAI Realtime] teardown_not_clean", {
        hasDc: Boolean(dcRef.current),
        hasPc: Boolean(pcRef.current),
        hasMedia: Boolean(mediaStreamRef.current),
        pendingQueueSize: pendingResponseInstructionsRef.current.length,
        pendingRequestIdsSize: pendingScriptedRequestIdsRef.current.size,
        pendingRequestTextSize: pendingScriptedRequestTextByIdRef.current.size,
        expectedByResponseSize: scriptedExpectedTextByResponseIdRef.current.size,
        activeAllowedResponseId: activeAllowedResponseIdRef.current,
        remoteAudioCount: remoteAudioElementsRef.current.length,
      });
    } else {
      console.warn("[OpenAI Realtime] teardown_clean");
    }
    setIsActive(false);
    setIsStarting(false);
    setIsListening(false);
    setIsSpeaking(false);
    setAudioConfirmed(false);
  }, [clearPostDrainOpenTimer, clearTerminalDrainTimer, sendOutputAudioBufferClear, sendResponseCancel, setAudioGateClosed, setMicrophoneCaptureEnabled, stopAllRemoteAudio]);

  stopConversationRef.current = stopConversation;

  useEffect(() => {
    return () => {
      stopConversationRef.current?.();
    };
  }, []);

  const startConversation = useCallback(async () => {
    if (startInFlightRef.current) {
      return;
    }
    setEndConversationAcknowledged(false);
    if (!window.RTCPeerConnection || !navigator.mediaDevices?.getUserMedia) {
      setError("WebRTC or microphone is not available in this browser.");
      return;
    }
    if (pcRef.current || dcRef.current || mediaStreamRef.current) {
      stopConversation();
    }
    startInFlightRef.current = true;
    const sessionToken = sessionTokenRef.current + 1;
    sessionTokenRef.current = sessionToken;
    setError(null);
    sessionHaltedRef.current = false;
    setIsStarting(true);
    setIsActive(true);
    try {
      // Match Gemini Live: compat `firebaseApp.functions(region).httpsCallable` so the call uses
      // the same Functions instance as shared-services (emulator + project). Modular-only
      // `httpsCallable(getFunctions(), …)` can hit the wrong backend; wrong/missing endpoint
      // often surfaces as a CORS preflight failure in the browser.
      await ensureFirebaseUserForLiveVoice();
      const fn = firebaseApp.functions(FUNCTIONS_REGION).httpsCallable("createOpenAIRealtimeClientSecret");
      const { data } = await fn({});
      const clientSecret = data?.clientSecret;
      if (!clientSecret || typeof clientSecret !== "string") {
        throw new Error("No client secret returned from server");
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      dc.addEventListener("message", (ev) => {
        if (sessionToken !== sessionTokenRef.current) return;
        handleDataMessage(ev.data);
      });
      dc.addEventListener("open", () => {
        if (sessionToken !== sessionTokenRef.current) return;
        setAudioConfirmed(true);
        setIsListening(true);
        setMicrophoneCaptureEnabled(true, "session_open");
        setIsStarting(false);
        sendSessionUpdate();
        setTimeout(() => {
          if (sessionToken !== sessionTokenRef.current) return;
          const opener = String(openingMessage || "").trim();
          if (opener) {
            setIsSpeaking(true);
            setIsListening(false);
            setMicrophoneCaptureEnabled(false, "opening_prompt");
            postOpeningDisclaimerPendingRef.current = Boolean(askDisclaimerAfterOpeningRef.current);
            sendModelResponse(opener, { turnSeq: 0 });
          }
          if (askFirstQuestionAfterOpening && !askDisclaimerAfterOpening) {
            setTimeout(() => {
              void runUserTurn("", "ask_first_after_opening");
            }, OPENING_RESPONSE_DELAY_MS);
          }
        }, OPENING_RESPONSE_DELAY_MS);
      });

      pc.addEventListener("track", (ev) => {
        if (sessionToken !== sessionTokenRef.current) return;
        const el = document.createElement("audio");
        el.autoplay = true;
        el.muted = true;
        el.volume = 0;
        el.srcObject = ev.streams[0];
        remoteAudioElementsRef.current.push(el);
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpRes = await fetch(REALTIME_CALLS_URL, {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${clientSecret}`,
          "Content-Type": "application/sdp",
        },
      });
      if (!sdpRes.ok) {
        const t = await sdpRes.text();
        throw new Error(`Realtime WebRTC failed: ${sdpRes.status} ${t}`);
      }
      const answerSdp = await sdpRes.text();
      if (sessionToken !== sessionTokenRef.current) {
        return;
      }
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
    } catch (err) {
      console.error("[OpenAI Realtime] startConversation", err);
      setError(err?.message || String(err));
      stopConversation();
    } finally {
      startInFlightRef.current = false;
    }
  }, [
    askFirstQuestionAfterOpening,
    handleDataMessage,
    openingMessage,
    runUserTurn,
    sendSessionUpdate,
    setMicrophoneCaptureEnabled,
    sendModelResponse,
    stopConversation,
  ]);

  const conversationScrollRef = useRef(null);

  const speechSupported =
    typeof window !== "undefined" && !!window.RTCPeerConnection && !!navigator.mediaDevices?.getUserMedia;

  return {
    isActive,
    isStarting,
    isListening,
    isSpeaking,
    currentQuestion: "",
    questionsAsked,
    conversationLog,
    audioConfirmed,
    isAcceptingInput: isListening && !isSpeaking && audioConfirmed,
    speechSupported,
    error,
    endConversationAcknowledged,
    startConversation,
    stopConversation,
    conversationScrollRef,
    language,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
}

export default useOpenAIRealtimeVoiceConversationEngine;
